import { inject, Injectable, Injector } from '@angular/core';
import {
    AuthController,
    DocumentController,
    DocumentItemPartial,
    DocumentItemTypeEnum,
} from '@domain';
import { environment } from '@environments';
import { TranslateService } from '@ngx-translate/core';
import { RaToastService } from '@shared/components';
import { catchError, map, Observable, of, shareReplay, tap } from 'rxjs';
import { DocumentFieldsController } from 'src/app/domain/controllers/document/document-fields.controller';
import { EditDataService } from './_edit-data.service';

/** Item removido + o índice que ele ocupava em `session.items` (array completo). */
interface RemovedFieldEntry {
    index: number;
    item: DocumentItemPartial;
}

interface PendingRemoval {
    documentId: string;
    sessionId: string;
    /** Ids pedidos na remoção (a cascata de pasta é refeita pelo backend). */
    fieldIds: string[];
    /** Snapshot completo (inclui filhos de pasta), em ordem crescente de índice. */
    entries: RemovedFieldEntry[];
    timer: ReturnType<typeof setTimeout> | null;
    toastId: string | null;
    /** O usuário desfez esta remoção (distingue tap duplo de undo expirado). */
    undone: boolean;
}

/**
 * Remoção de campo com janela de desfazer.
 *
 * O campo sai da tela na hora (só localmente) e o `remove-fields` real é enviado
 * quando a janela expira ou num flush. Desfazer reinsere no índice original sem
 * nunca chamar a API.
 *
 * `flush()` é OBRIGATÓRIO antes de qualquer operação que dependa do estado do
 * servidor (reordenar, mover, preview/PDF, corrigir textos, remover seção, abrir
 * a lixeira, sair do editor) — senão o servidor ainda enxerga o campo removido.
 */
@Injectable({ providedIn: 'root' })
export class FieldRemovalService {
    /**
     * Janela em que a remoção é só local. Um pouco maior que o toast (5s) para o
     * Desfazer nunca chegar depois do commit enquanto o toast some.
     */
    public static readonly UNDO_WINDOW_MS = 6000;
    private static readonly TOAST_DURATION_MS = 5000;

    private _injector = inject(Injector);
    private _editDataService = inject(EditDataService);
    private _documentFieldsController = inject(DocumentFieldsController);
    private _documentController = inject(DocumentController);
    private _toast = inject(RaToastService);

    private _pending: PendingRemoval | null = null;
    private _commitInFlight: Observable<void> | null = null;

    private _t(key: string): string {
        return this._injector.get(TranslateService).instant(key);
    }

    /**
     * Ids em janela de desfazer (inclui filhos de pasta). O realtime usa isto
     * para um `session-fields-reload` não ressuscitar o campo antes do commit.
     */
    public get pendingFieldIds(): string[] {
        return this._pending?.entries.map(entry => entry.item._id) ?? [];
    }

    /**
     * Remove localmente NA HORA e agenda o `remove-fields` real para o fim da
     * janela de desfazer. O toast oferece Desfazer, que reinsere os itens no
     * índice original sem chamar a API.
     */
    public softDelete(fieldIds: string[]): void {
        if (!fieldIds?.length) return;

        const documentId = this._editDataService.getDocumentId();
        const sessionId = this._editDataService.getCurrentSessionId();
        if (!documentId || !sessionId) return;

        const session = this._editDataService.currentSession();
        const items = session?.items ?? [];
        const entries = this._collectEntries(items, fieldIds);
        if (!entries.length) return;

        // Uma remoção pendente por vez: a anterior é confirmada agora.
        this.flush().subscribe();

        const pending: PendingRemoval = {
            documentId,
            sessionId,
            fieldIds,
            entries,
            timer: null,
            toastId: null,
            undone: false,
        };

        const removedIds = entries.map(entry => entry.item._id);
        const remaining = items.filter(item => !removedIds.includes(item._id));
        this._editDataService.fieldsDeleted(removedIds, sessionId, this._sessionSize(remaining));
        // O PDF pronto deixou de refletir o documento (se o usuário desfizer, o
        // custo é só regerar) — evita baixar um PDF com o campo removido.
        this._editDataService.invalidatePdf();

        this._pending = pending;
        pending.timer = setTimeout(
            () => this._commit(pending).subscribe(),
            FieldRemovalService.UNDO_WINDOW_MS
        );

        pending.toastId = this._toast.show({
            type: 'info',
            variant: 'pill',
            // Plural pelo efeito real na tela: remover 1 pasta com filhos apaga
            // vários cards (entries inclui a cascata).
            message: this._t(
                entries.length === 1
                    ? 'document.edit.undoRemoval.fieldRemoved'
                    : 'document.edit.undoRemoval.fieldsRemoved'
            ),
            duration: FieldRemovalService.TOAST_DURATION_MS,
            action: {
                label: this._t('common.undo'),
                handler: () => this._undo(pending),
            },
        });
    }

    /**
     * Confirma imediatamente a remoção pendente, se houver, e aguarda um commit
     * já em voo (o timer pode ter disparado com o HTTP ainda pendente).
     */
    public flush(): Observable<void> {
        const pending = this._pending;
        if (!pending) return this._commitInFlight ?? of(void 0);
        return this._commit(pending);
    }

    /**
     * Envia a remoção pendente durante o `beforeunload`, quando não há tempo
     * para um round-trip normal: `keepalive` deixa a request sobreviver ao
     * fechamento da aba. Sem isto o timer morre com a página e o campo
     * "ressuscitaria" na próxima abertura do documento.
     */
    public commitPendingWithKeepalive(): void {
        const pending = this._pending;
        if (!pending) return;

        if (pending.timer) clearTimeout(pending.timer);
        this._pending = null;

        // Resolvido só aqui: o AuthController abre conexão de realtime na
        // construção, e este serviço é injetado por toda a tela de edição.
        const token = this._injector.get(AuthController).getTokenInCookie();
        fetch(`${environment.API_BASE}document/remove-fields`, {
            method: 'PATCH',
            headers: {
                ...(token && { Authorization: token }),
                'Content-Type': 'application/json',
            },
            body: JSON.stringify({
                documentId: pending.documentId,
                sessionId: pending.sessionId,
                fieldIds: pending.fieldIds,
            }),
            keepalive: true,
        }).catch(() => {});
    }

    /**
     * Um `field-updated` de outro usuário pode chegar enquanto o campo está em
     * janela de desfazer (removido só localmente; o servidor ainda o tem).
     * Atualiza o snapshot para o Desfazer reinserir o conteúdo mais novo.
     */
    public applyRtUpdateToPendingSnapshot(field: DocumentItemPartial): void {
        const entries = this._pending?.entries;
        if (!entries || !field?._id) return;

        const entry = entries.find(item => item.item._id === field._id);
        if (!entry) return;

        entry.item = {
            ...field,
            parentFolderId: field.parentFolderId ?? entry.item.parentFolderId,
        };
    }

    /**
     * Snapshot (item + índice absoluto) de tudo que a remoção vai tirar,
     * incluindo a cascata de pasta (CHECKLIST_FOLDER → filhos por
     * parentFolderId), espelhando o backend.
     */
    private _collectEntries(items: DocumentItemPartial[], fieldIds: string[]): RemovedFieldEntry[] {
        const ids = new Set(fieldIds);
        const folderIds = new Set(
            items
                .filter(
                    item => ids.has(item._id) && item.type === DocumentItemTypeEnum.CHECKLIST_FOLDER
                )
                .map(item => item._id)
        );

        const entries: RemovedFieldEntry[] = [];
        items.forEach((item, index) => {
            const isFolderChild = !!item.parentFolderId && folderIds.has(item.parentFolderId);
            if (ids.has(item._id) || isFolderChild) {
                // Cópia: o objeto original pode ser mutado por outros fluxos.
                entries.push({ index, item: { ...item, selected: false } });
            }
        });

        return entries;
    }

    /**
     * Desfaz a remoção pendente (clique no Desfazer): cancela o timer e reinsere
     * os itens. A API nunca chega a ser chamada.
     */
    private _undo(pending: PendingRemoval): void {
        if (pending.toastId) this._toast.dismiss(pending.toastId);

        if (this._pending !== pending) {
            // Clique duplo depois de um desfazer bem-sucedido: silencioso.
            // Commit já rodado (janela expirada ou flush por outra operação):
            // avisa em vez de falhar em silêncio.
            if (!pending.undone) {
                this._toast.success({
                    message: this._t('document.edit.undoRemoval.undoExpired'),
                });
            }
            return;
        }

        pending.undone = true;
        if (pending.timer) clearTimeout(pending.timer);
        this._pending = null;

        this._restoreEntries(pending);

        // Mesma variante do toast de remoção: a confirmação troca a ilha no lugar.
        this._toast.success({
            message: this._t('document.edit.undoRemoval.removalUndone'),
            variant: 'pill',
        });
    }

    /** Reinsere o snapshot nos índices originais (desfazer ou commit rejeitado). */
    private _restoreEntries(pending: PendingRemoval): void {
        // Sempre a seção DA REMOÇÃO: o usuário pode ter trocado de seção durante
        // a janela, e `currentSession()` já seria outra (dedupe e tamanho sairiam
        // da seção errada).
        const target = this._sessionItems(pending.sessionId);
        const existingIds = new Set(target.map(item => item._id));

        // Dedupe por id: um eco de RT (ex.: field-restored) pode já ter
        // devolvido o item.
        const fields = pending.entries
            .filter(entry => !existingIds.has(entry.item._id))
            .map(entry => ({ value: entry.item, index: entry.index }));
        if (!fields.length) return;

        const restored = [...target, ...fields.map(field => field.value)];
        this._editDataService.fieldsCreated(fields, this._sessionSize(restored), pending.sessionId);
    }

    /** Itens de uma seção pelo id (independe da seção aberta na tela). */
    private _sessionItems(sessionId: string): DocumentItemPartial[] {
        const fromDocument = this._editDataService
            .document()
            ?.sessions?.find(section => section._id === sessionId);
        if (fromDocument) return fromDocument.items ?? [];

        const current = this._editDataService.currentSession();
        return current?._id === sessionId ? (current.items ?? []) : [];
    }

    /**
     * Confirma a remoção no servidor. Erro REAL re-sincroniza a seção (o
     * servidor é a fonte da verdade: outro usuário pode ter removido o campo);
     * sem rede para o refetch, restaura o snapshot para não perder o conteúdo.
     */
    private _commit(pending: PendingRemoval): Observable<void> {
        if (this._pending !== pending) return this._commitInFlight ?? of(void 0);

        if (pending.timer) clearTimeout(pending.timer);
        this._pending = null;

        const commit$ = this._documentFieldsController
            .removeFields(pending.documentId, pending.sessionId, pending.fieldIds)
            .pipe(
                catchError(() => this._resyncAfterFailedCommit(pending)),
                map(() => void 0),
                shareReplay({ bufferSize: 1, refCount: false })
            );

        this._commitInFlight = commit$;
        commit$.subscribe({
            complete: () => {
                if (this._commitInFlight === commit$) this._commitInFlight = null;
            },
        });

        return commit$;
    }

    private _resyncAfterFailedCommit(pending: PendingRemoval): Observable<void> {
        return this._documentController.getById(pending.documentId).pipe(
            tap(fresh =>
                // Uma remoção NOVA pode ter entrado na janela nesse meio-tempo:
                // o refetch não pode ressuscitar o campo dela.
                this._editDataService.reloadSessionFields(
                    pending.sessionId,
                    fresh,
                    this.pendingFieldIds
                )
            ),
            map(() => void 0),
            catchError(() => {
                // Refetch falhou (rede): devolve os itens para não perder conteúdo.
                this._restoreEntries(pending);
                return of(void 0);
            })
        );
    }

    private _sessionSize(items: DocumentItemPartial[]): number {
        return items.reduce(
            (total, item) => total + (Number.isFinite(item.size) ? item.size : 0),
            0
        );
    }
}
